import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  DataTable,
  Button,
  TextField,
  Banner,
  Stack,
  Text,
  Heading,
  Modal,
  TextContainer,
  Badge,
  Spinner,
  Frame,
  Toast,
  ContextualSaveBar,
  Layout,
  FormLayout,
  ButtonGroup,
  Icon,
  Tooltip,
  EmptyState,
  Link
} from '@shopify/polaris';
import { DeleteMajor, DuplicateMinor } from '@shopify/polaris-icons';
import { BetaInviteService } from '../../services/betaInviteService';
import { useAuth } from '../../contexts/AuthContext';

function BetaManagementPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [betaCodes, setBetaCodes] = useState([]);
  const [betaModeEnabled, setBetaModeEnabled] = useState(false);
  const [description, setDescription] = useState('');
  const [generatingCode, setGeneratingCode] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  
  const fetchBetaCodes = useCallback(async () => {
    try {
      setLoading(true);
      const codes = await BetaInviteService.getAllBetaCodes();
      setBetaCodes(codes);
      
      // Check if beta mode is enabled
      const betaStatus = await BetaInviteService.isBetaModeEnabled();
      setBetaModeEnabled(betaStatus);
    } catch (error) {
      console.error('Error fetching beta codes:', error);
      showToast('Error loading beta codes', true);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchBetaCodes();
  }, [fetchBetaCodes]);
  
  const toggleBetaMode = async (enabled) => {
    try {
      // Use a fallback admin ID if currentUser is not available
      const adminId = currentUser?.uid || 'system_admin';
      await BetaInviteService.toggleBetaMode(enabled, adminId);
      setBetaModeEnabled(enabled);
      showToast(`Beta mode ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error toggling beta mode:', error);
      showToast('Error toggling beta mode', true);
    }
  };
  
  const generateBetaCode = async () => {
    try {
      setGeneratingCode(true);
      // Use a fallback admin ID if currentUser is not available
      const adminId = currentUser?.uid || 'system_admin';
      const result = await BetaInviteService.generateBetaCode(adminId, description);
      showToast('Beta code generated successfully');
      setDescription('');
      fetchBetaCodes();
    } catch (error) {
      console.error('Error generating beta code:', error);
      showToast('Error generating beta code', true);
    } finally {
      setGeneratingCode(false);
    }
  };
  
  const deleteBetaCode = async (codeId) => {
    try {
      await BetaInviteService.deleteBetaCode(codeId);
      showToast('Beta code deleted successfully');
      fetchBetaCodes();
    } catch (error) {
      console.error('Error deleting beta code:', error);
      showToast('Error deleting beta code', true);
    }
  };
  
  const showToast = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setToastActive(true);
  };
  
  const handleCopyLink = (code) => {
    setSelectedCode(code);
    setShowLinkModal(true);
    setCopiedToClipboard(false);
  };
  
  const copyToClipboard = () => {
    if (!selectedCode) return;
    
    const betaLink = `${window.location.origin}/register?beta=${selectedCode.code}`;
    navigator.clipboard.writeText(betaLink)
      .then(() => {
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy link:', err);
        showToast('Failed to copy link to clipboard', true);
      });
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      
      // Format the date using toLocaleDateString
      const formattedDate = date.toLocaleDateString();
      
      // Calculate time ago in a simple way
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      let timeAgo;
      if (diffDays > 30) {
        const diffMonths = Math.floor(diffDays / 30);
        timeAgo = `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
      } else if (diffDays > 0) {
        timeAgo = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      } else if (diffHours > 0) {
        timeAgo = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      } else if (diffMins > 0) {
        timeAgo = `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
      } else {
        timeAgo = `${diffSecs} second${diffSecs === 1 ? '' : 's'} ago`;
      }
      
      return `${formattedDate} (${timeAgo})`;
    } catch (e) {
      return dateString;
    }
  };
  
  const renderStatus = (isUsed) => {
    return isUsed ? 
      <Badge status="warning">Used</Badge> : 
      <Badge status="success">Available</Badge>;
  };
  
  const rows = betaCodes.map((code) => [
    code.code,
    code.description || 'N/A',
    renderStatus(code.isUsed),
    formatDate(code.createdAt),
    code.isUsed ? formatDate(code.usedAt) : 'N/A',
    code.isUsed ? (code.userEmail || 'N/A') : 'N/A',
    <ButtonGroup segmented>
      <Tooltip content="Copy signup link">
        <Button 
          icon={<Icon source={DuplicateMinor} />} 
          onClick={() => handleCopyLink(code)} 
          disabled={code.isUsed}
        />
      </Tooltip>
      <Tooltip content="Delete code">
        <Button 
          icon={<Icon source={DeleteMajor} />} 
          onClick={() => deleteBetaCode(code.id)} 
          destructive
        />
      </Tooltip>
    </ButtonGroup>
  ]);
  
  return (
    <Frame>
      {toastActive && (
        <Toast 
          content={toastMessage} 
          error={toastError} 
          onDismiss={() => setToastActive(false)} 
        />
      )}
      
      <Page 
        title="Beta Program Management"
        subtitle="Generate and manage beta invitation codes"
      >
        <Layout>
          <Layout.Section>
            <Card>
              <Card.Section>
                <Stack distribution="equalSpacing" alignment="center">
                  <Stack.Item>
                    <Heading>Beta Mode</Heading>
                  </Stack.Item>
                  <Stack.Item>
                    <Button
                      onClick={() => toggleBetaMode(!betaModeEnabled)}
                      primary={betaModeEnabled}
                    >
                      {betaModeEnabled ? 'Enabled' : 'Disabled'}
                    </Button>
                  </Stack.Item>
                </Stack>
              </Card.Section>
              
              <Card.Section>
                <Banner status={betaModeEnabled ? "info" : "success"}>
                  <p>
                    Beta mode is currently <strong>{betaModeEnabled ? 'enabled' : 'disabled'}</strong>. 
                    {betaModeEnabled 
                      ? ' When enabled, new users can only register with a valid beta code.' 
                      : ' When disabled, users can register normally without a beta code.'}
                  </p>
                </Banner>
              </Card.Section>
            </Card>
          </Layout.Section>
          
          <Layout.Section>
            <Card title="Generate Beta Code">
              <Card.Section>
                <FormLayout>
                  <TextField
                    label="Description (optional)"
                    value={description}
                    onChange={setDescription}
                    placeholder="e.g., For early testers, Marketing campaign, etc."
                    helpText="Add a description to help you remember who this code is for"
                  />
                  <Button 
                    primary 
                    onClick={generateBetaCode} 
                    loading={generatingCode}
                    disabled={generatingCode}
                  >
                    Generate New Beta Code
                  </Button>
                </FormLayout>
              </Card.Section>
            </Card>
          </Layout.Section>
          
          <Layout.Section>
            <Card title="Beta Invitation Codes">
              {loading ? (
                <Card.Section>
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spinner size="large" />
                    <div style={{ marginTop: '1rem' }}>
                      <Text>Loading beta codes...</Text>
                    </div>
                  </div>
                </Card.Section>
              ) : betaCodes.length === 0 ? (
                <Card.Section>
                  <EmptyState
                    heading="No beta codes yet"
                    image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  >
                    <p>Generate beta codes to invite users to your application.</p>
                  </EmptyState>
                </Card.Section>
              ) : (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text'
                  ]}
                  headings={[
                    'Code',
                    'Description',
                    'Status',
                    'Created',
                    'Used Date',
                    'Used By',
                    'Actions'
                  ]}
                  rows={rows}
                  footerContent={`Showing ${betaCodes.length} beta code${betaCodes.length === 1 ? '' : 's'}`}
                />
              )}
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
      
      <Modal
        open={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        title="Beta Invitation Link"
        primaryAction={{
          content: copiedToClipboard ? 'Copied!' : 'Copy Link',
          onAction: copyToClipboard,
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: () => setShowLinkModal(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Share this link with users you want to invite to the beta program:</p>
            <TextField
              value={selectedCode ? `${window.location.origin}/auth/register?beta=${selectedCode.code}` : ''}
              readOnly
              autoComplete="off"
              connectedRight={<Button onClick={copyToClipboard}>{copiedToClipboard ? 'Copied!' : 'Copy'}</Button>}
            />
            <p style={{ marginTop: '1rem' }}>
              <Text color="subdued">
                This link contains a single-use beta code. Once used, it will no longer be valid for new registrations.
              </Text>
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>
    </Frame>
  );
}

export default BetaManagementPage;
